import { AttributeEditorDialogViewModel } from '@Alternatives/Attributes/Components/AttributeEditorDialog';
import AttributeEditorDialog from '@Alternatives/Attributes/Components/AttributeEditorDialog.vue';
import AttributesQueries from '@Alternatives/Attributes/Domain/queries-vue-component';
import { AttributeEditorReadModel, AttributeReadModel } from '@Alternatives/Attributes/Domain/readmodels';
import { generateName, strCompareLowerCase } from "@common/Common";
import GoalNavigation from '@common/Components/GoalNavigation.vue';
import PortfolioOrDecisionNavigation from "@common/Components/PortfolioOrDecisionNavigation.vue";
import { ICommandStatus } from '@cqrs/cqrs';
import { ICriteriaCommand } from '@Criteria/cqrs';
import { CriterionScaleEditorViewModel } from '@Criteria/Hierarchies/Components/CriterionScaleEditor';
import CriterionScaleEditor from '@Criteria/Hierarchies/Components/CriterionScaleEditor.vue';
import { AssignAttributeMeasurementCommandV1, AssignCustomScaleMeasurementCommandV1, AssignPairwiseMeasurementCommandV1, AssignScaleTemplateMeasurementCommandV1 } from '@Criteria/Hierarchies/Domain/commands-core';
import CriteriaHierarchyCommands from "@Criteria/Hierarchies/Domain/commands-vue-component";
import CriteriaHierarchyQueries from "@Criteria/Hierarchies/Domain/queries-vue-component";
import { CriterionReadModel, LegacyMeasurementsPageReadModel } from "@Criteria/Hierarchies/Domain/readmodels";
import { DiscreteScaleEditorViewModel } from '@Scales/Components/DiscreteScaleEditor';
import DiscreteScaleEditor from '@Scales/Components/DiscreteScaleEditor.vue';
import { CreateWorkspaceScaleCommandV1 } from '@Scales/Domain/commands-core';
import ScalesCommands from '@Scales/Domain/commands-vue-component';
import ScalesQueries from '@Scales/Domain/queries-vue-component';
import { DiscreteScaleLevelReadModel, DiscreteScaleReadModel, WorkspaceScalesListReadModel } from '@Scales/readmodels';
import { DiscreteScaleLevel } from "@Scales/Model";
import { Component, mixins } from 'vue-facing-decorator';


@Component({
    components: { GoalNavigation, DiscreteScaleEditor, PortfolioOrDecisionNavigation, AttributeEditorDialog, CriterionScaleEditor }
})
export default class LegacyMeasurementsPage extends mixins(CriteriaHierarchyQueries, CriteriaHierarchyCommands, ScalesQueries, AttributesQueries, ScalesCommands)
{
    get workspaceId(): number { return Number(this.$route?.params?.workspaceId ?? 0); }
    get projectId(): number { return Number(this.$route?.params?.projectId ?? 0); }
    get evalModelId(): number { return Number(this.$route?.params?.evalModelId ?? 0); }

    rm: LegacyMeasurementsPageReadModel | null = null;

    Initialized: boolean = false;
    Attributes: AttributeReadModel[] = [];
    ScalesTemplates: DiscreteScaleReadModel[] = [];
    Criteria: CriterionVM[] = [];
    SearchPhrase: string | null = null;

    attributeEditorDialog: AttributeEditorDialogViewModel = new AttributeEditorDialogViewModel();
    criterionScaleEditor = new CriterionScaleEditorViewModel();
    discreteScaleEditor = new DiscreteScaleEditorViewModel();

    get NoneCriteriaSelected(): boolean { return this.Criteria.some(a => a.IsSelected) === false; }
    get AnyCriteriaSelected(): boolean { return this.Criteria.some(a => a.IsSelected); }
    get AllCriteriaSelected(): boolean { return this.Criteria.some(a => a.IsSelected === false) === false; }
    get SurveyBased(): CriterionVM[] { return this.CriteriaView.filter(a => a.Attribute == null); }
    get AttributeBased(): CriterionVM[] { return this.CriteriaView.filter(a => a.Attribute != null); }
    get CriteriaView(): CriterionVM[]
    {
        let view = this.Criteria;

        if (this.SearchPhrase)
        {
            const phrase = this.SearchPhrase.toLowerCase();
            view = view.filter(a => (a.Name + " " + a.Path).toLowerCase().indexOf(phrase) >= 0);
        }

        return view;
    }

    selectAll() { this.CriteriaView.forEach(a => a.IsSelected = true); }
    unselectAll() { this.CriteriaView.forEach(a => a.IsSelected = false); }
    inverseSelection() { this.CriteriaView.forEach(a => a.IsSelected = !a.IsSelected); }

    showAttributeCreator()
    {
        this.attributeEditorDialog.showCreator({
            defaultName: generateName("Attribute"),
            allowChangeAttributeType: false,
            allowMultipleValuesEnabled: false,
            allowInputTypeEvaluate: true,
        });

        this.runAttributeEditorQueryV1(this.workspaceId, this.projectId, null);
    }

    showCriterionScaleEditor(vm: CriterionVM)
    {
        this.criterionScaleEditor.show(vm.CriterionId, vm.Name, vm.Description, vm.ScaleLevels);
    }

    showWorkspaceScaleCreator(vm: CriterionVM | null)
    {
        const name = generateName("Scale");

        const levels = vm != null ? vm.ScaleLevels.map(a => 
        {
            const lvlRm = new DiscreteScaleLevelReadModel();
            lvlRm.Name = a.Name;
            lvlRm.Value = a.Value;
            lvlRm.Definition = a.Definition;

            return lvlRm;
        }) : [new DiscreteScaleLevelReadModel(), new DiscreteScaleLevelReadModel(), new DiscreteScaleLevelReadModel()];

        this.discreteScaleEditor.showCreator(name, null, levels);
    }



    numericAttributeCreated(attrId: number, maxValue: number | null)
    {
        this.runAttributeByIdQueryV1(this.workspaceId, this.projectId, this.rm.AlternativesListId, attrId);
    }

    handleAssignCustomScaleMeasurementCommand(criterionId: number, lvls: DiscreteScaleLevel[])
    {
        this.invokeAssignCustomScaleMeasurementCommandV1(this.workspaceId, this.rm.CriteriaHierarchyId, criterionId, lvls);
    }


    handleAssignPairwiseMeasurementCommand(vm: CriterionVM)
    {
        vm.Loading = true;
        vm.AttributeId = null;
        vm.ScaleLevels = null;
        this.invokeAssignPairwiseMeasurementCommandV1(this.workspaceId, this.rm.CriteriaHierarchyId, vm.CriterionId);
    }

    handleBulkAssignPairwiseMeasurementCommand()
    {
        const criteria = this.Criteria
            .filter(a => a.IsSelected)
            .filter(a => a.HasScale || a.Attribute != null);

        for (const vm of criteria)
        {
            this.handleAssignPairwiseMeasurementCommand(vm);
        }
    }

    handleAssignAttributeMeasurementCommand(vm: CriterionVM, attrId: number)
    {
        vm.Loading = true;
        vm.AttributeId = attrId;
        vm.ScaleLevels = [];
        this.invokeAssignAttributeMeasurementCommandV1(this.workspaceId, this.rm.CriteriaHierarchyId, vm.CriterionId, attrId);
    }

    handleAssignScaleTemplateMeasurementCommandV1(vm: CriterionVM, scale: DiscreteScaleReadModel)
    {
        vm.Loading = true;
        vm.ScaleLevels = scale.Levels;
        vm.AttributeId = null;
        this.invokeAssignScaleTemplateMeasurementCommandV1(this.workspaceId, this.rm.CriteriaHierarchyId, vm.CriterionId, scale.Id);
    }

    handleBulkAssignScaleTemplateMeasurementCommandV1(scale: DiscreteScaleReadModel)
    {
        const criteria = this.Criteria.filter(a => a.IsSelected);

        for (const vm of criteria)
        {
            this.handleAssignScaleTemplateMeasurementCommandV1(vm, scale);
        }
    }

    handleCreateWorkspaceScaleCommand(name: string, description: string | null, levels: string)
    {
        this.invokeCreateWorkspaceScaleCommandV1(this.workspaceId, 0, name, description, levels);
    }


    mounted()
    {
        document.title = "Measurements | TransparentChoice";
        this.runLegacyMeasurementsPageQueryV1(this.workspaceId, this.projectId, this.evalModelId);
        this.runWorkspaceScalesListQueryV1(this.workspaceId);
    }

    onSetModel(model: any, modelName: string)
    {
        if (modelName == "LegacyMeasurementsPageReadModel")
        {
            this.rm = model as LegacyMeasurementsPageReadModel;

            this.Attributes = this.rm.Attributes.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
            this.Criteria = this.rm.Criteria.map(a => new CriterionVM(a, this));

            this.Initialized = true;
        }
        if (modelName == "WorkspaceScalesListReadModel")
        {
            const rm = model as WorkspaceScalesListReadModel;
            this.ScalesTemplates = rm.Scales.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
        }
        else if (modelName == "CriterionReadModel")
        {
            const rm = model as CriterionReadModel;
            const c = this.Criteria.find(a => a.CriterionId === rm.CriterionId);
            c.ScaleLevels = rm.ScaleLevels;
        }
        else if (modelName == "AttributeReadModel")
        {
            const attr = model as AttributeReadModel;
            this.Attributes = this.Attributes.filter(a => a.Id !== attr.Id);
            this.Attributes.push(attr);
        }
        else if (modelName === "AttributeEditorReadModel")
        {
            const rm = model as AttributeEditorReadModel;
            this.attributeEditorDialog.setData(rm);
        }
    }

    onCommandSucceed(cmd: ICriteriaCommand, correlationId: string, result: any)
    {
        if (AssignPairwiseMeasurementCommandV1.matchType(cmd))
        {
            const criterionId = Number.parseInt(correlationId);
            this.Criteria.find(a => a.CriterionId === criterionId).Loading = false;
        }
        else if (AssignAttributeMeasurementCommandV1.matchType(cmd))
        {
            const criterionId = Number.parseInt(correlationId.split(".")[0]);
            this.Criteria.find(a => a.CriterionId === criterionId).Loading = false;
        }
        else if (AssignScaleTemplateMeasurementCommandV1.matchType(cmd))
        {
            const criterionId = Number.parseInt(correlationId);
            this.Criteria.find(a => a.CriterionId === criterionId).Loading = false;
        }
        else if (AssignCustomScaleMeasurementCommandV1.matchType(cmd))
        {
            const criterionId = Number.parseInt(correlationId);
            this.runCriterionQueryV1(this.workspaceId, this.projectId, this.rm.CriteriaHierarchyId, criterionId);

            this.criterionScaleEditor.close();
        }
        else if (CreateWorkspaceScaleCommandV1.matchType(cmd))
        {
            this.ScalesTemplates = [];
            this.runWorkspaceScalesListQueryV1(this.workspaceId);

            this.discreteScaleEditor.close();
        }
    }

    onCommandFailed(status: ICommandStatus)
    {
        if (AssignCustomScaleMeasurementCommandV1.matchType(status.Command))
        {
            this.criterionScaleEditor.displayValidationErrors(status.ValidationErrors);
        }
        else if (CreateWorkspaceScaleCommandV1.matchType(status.Command))
        {
            this.discreteScaleEditor.displayValidationErrors(status.ValidationErrors);
        }
    }
}

class CriterionVM
{
    Page: LegacyMeasurementsPage;

    CriterionId: number | null = null;
    Name: string | null = null;
    Description: string | null = null;
    Path: string | null = null;
    AttributeId: number | null = null;
    IsSelected: boolean = false;
    Loading: boolean = false;
    ScaleLevels: DiscreteScaleLevel[] = [];

    get Attribute(): AttributeReadModel | null
    {
        if (this.AttributeId)
        {
            return this.Page.Attributes.find(a => a.Id === this.AttributeId) ?? null;
        }

        return null;
    }

    get MeasurementLabel(): string 
    {
        if (this.Attribute != null)
        {
            return `Attribute: ${this.Attribute.Name}`;
        }
        else if (this.HasScale)
        {
            const lvls = this.ScaleLevels.map(a => `${a.Name} (${a.Value})`).join("; ");
            return `Scale: ${lvls}`
        }
        else
        {
            return "Pairwise comparisons";
        }
    }

    get HasScale(): boolean { return this.ScaleLevels != null; }

    constructor(criterion: CriterionReadModel, page: LegacyMeasurementsPage)
    {
        this.Page = page;

        this.CriterionId = criterion.CriterionId;
        this.Name = criterion.Name;
        this.Description = criterion.Description;
        this.Path = criterion.Path;

        this.AttributeId = criterion.AttributeId;
        this.ScaleLevels = criterion.ScaleLevels;
    }
}